import { Button, Space, Table, TableColumnsType, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import {
	ApplicationSummary,
	ApplicationTableDataType,
	Pagination,
	PagingConfig,
	UserRole,
} from '../../../shared/VolunteerApplicationServiceTypes';
import {
	displayPreworkRecommendationStatus,
	displayPreworkStatus,
	firstLetterUpperCase,
	getFullName,
	hasPrivilege,
	tablePaginationConfiguration,
} from '../../../shared/Utils';
import {
	ApplicationPreworkStatus,
	PreworkCompletionStatus,
	PreworkReviewAction,
} from '../../../shared/PreWorkServiceTypes';
import moment from 'moment';
import { InfoCircleFilled } from '@ant-design/icons';
import { DATE_DO_MMM_YYYY } from '../../../shared/Constants';
import useUserSession from '../../../hooks/useUserSession';
import { PrivilegeCodes } from '../../../shared/RoleConstants';

interface ApplicationSummaryTableProps {
	applications: ApplicationPreworkStatus[] | undefined;
	onEvaluateClick: (application: ApplicationSummary) => void;
	onPageChange: (page: number, pageSize: number) => void;
	pageConfig: PagingConfig;
	pagination: Pagination;
}

export const ApplicationSummaryTable = (
	props: ApplicationSummaryTableProps
) => {
	const {
		applications,
		onEvaluateClick,
		onPageChange,
		pageConfig,
		pagination,
	} = props;
	const { t } = useTranslation();
	const { selectedRole, loggedInUserPrivileges } = useUserSession();
	const isApprover = selectedRole === UserRole.APPROVER;

	const tablePaginationConfig = tablePaginationConfiguration(
		pageConfig,
		pagination,
		onPageChange
	);

	const handleEvaluateClick = (application: ApplicationTableDataType) => {
		onEvaluateClick(application.originalApplication);
	};

	const applicationTableData = applications?.map(
		({
			application,
			preworkCompletionStatus,
			preworkMentorCompletionStatus,
			preworkMentorNextAttemptDate,
			preworkMentor,
			approverPreworkRecommendation,
			approverComment,
			isForwardedToApprover,
		}) => {
			const {
				applicant: { srcmid, gender = '' },
				center: { zone },
			} = application;
			const preworkStatus = t(
				displayPreworkStatus(
					preworkCompletionStatus,
					preworkMentorCompletionStatus
				)
			);
			const recommendationStatus = t(
				displayPreworkRecommendationStatus(approverPreworkRecommendation)
			);
			const tableData: ApplicationTableDataType = {
				key: srcmid,
				fullName: getFullName(application.applicant),
				idCard: srcmid,
				gender,
				zone,
				originalApplication: application,
				preworkStatus,
				originalMentorPreworkStatus: preworkMentorCompletionStatus,
				disableViewAction:
					(preworkMentorNextAttemptDate &&
						moment().isSameOrBefore(preworkMentorNextAttemptDate)) ||
					preworkCompletionStatus !== PreworkCompletionStatus.COMPLETED ||
					!preworkCompletionStatus ||
					(isForwardedToApprover &&
						approverPreworkRecommendation !==
							PreworkReviewAction.EXTEND_TO_PREWORK_MENTOR),
				preworkMentorNextAttemptDate,
				preworkMentorName: getFullName(preworkMentor),
				preworkMentorSrcmId: preworkMentor?.srcmId,
				approverRecommendationStatus: approverPreworkRecommendation
					? recommendationStatus
					: 'N/A',
				originalApproverRecommendationStatus: approverPreworkRecommendation,
				approverComments: approverComment || 'N/A',
			};

			return tableData;
		}
	);

	const baseColumns: TableColumnsType<ApplicationTableDataType> = [
		{
			title: t('fullName'),
			dataIndex: 'fullName',
			key: 'fullName',
			className:
				'max-w-[400px] border-r-0 whitespace-nowrap md:whitespace-normal break-words',
		},
		{
			title: t('idCard'),
			dataIndex: 'idCard',
			key: 'idCard',
		},
		{
			title: t('gender'),
			dataIndex: 'gender',
			key: 'gender',
			render: (__, record, index) => (
				<span key={index}>
					<span className="ml-2">{firstLetterUpperCase(record?.gender)}</span>
				</span>
			),
		},
		{
			title: t('zone'),
			dataIndex: 'zone',
			key: 'zone',
			className:
				'max-w-[400px] border-r-0 whitespace-nowrap md:whitespace-normal break-words',
		},
		{
			title: t('approverStatus'),
			dataIndex: 'approverRecommendationStatus',
			key: 'approverRecommendationStatus',
		},
	];

	if (isApprover) {
		baseColumns.push(
			{
				title: t('preworkMentor'),
				dataIndex: 'preworkMentorName',
				key: 'preworkMentorName',
				className:
					'max-w-[300px] border-r-0 whitespace-nowrap md:whitespace-normal break-words',
			},
			{
				title: t('mentorSrcmId'),
				dataIndex: 'preworkMentorSrcmId',
				key: 'preworkMentorSrcmId',
			}
		);
	} else {
		baseColumns.push(
			{
				title: t('approverComments'),
				dataIndex: 'approverComments',
				key: 'approverComments',
				width: 200,
				className:
					'max-w-[200px] border-r-0 whitespace-nowrap md:whitespace-normal break-words',
				render: (text: string) => (
					<Tooltip title={text} placement="topLeft">
						<span className="inline-block max-w-[150px] align-top text-ellipsis overflow-hidden whitespace-nowrap">
							{text}
						</span>
					</Tooltip>
				),
			},
			{
				title: t('preworkStatus'),
				dataIndex: 'preworkStatus',
				key: 'preworkStatus',
			}
		);
	}

	const showPreworkMentorEvaluate = hasPrivilege(
		PrivilegeCodes.CAPTURE_PREWORK_MENTOR_ANSWERS,
		loggedInUserPrivileges
	);

	baseColumns.push({
		title: t('action'),
		key: 'operation',
		fixed: 'right',
		width: 150,
		render: (__, record) => {
			const resubmitDate = moment(record.preworkMentorNextAttemptDate).format(
				DATE_DO_MMM_YYYY
			);
			const showInfoIcon =
				record.disableViewAction &&
				(record.originalMentorPreworkStatus ===
					PreworkCompletionStatus.INCOMPLETE ||
					record.originalMentorPreworkStatus ===
						PreworkCompletionStatus.PARTIALLY_COMPLETED) &&
				record.originalApproverRecommendationStatus !==
					PreworkReviewAction.PREWORK_APPROVE &&
				record.originalApproverRecommendationStatus !==
					PreworkReviewAction.PREWORK_REJECT;
			return (
				<Space size="middle" className="whitespace-nowrap">
					{isApprover ? (
						<div className="flex gap-2 justify-center">
							{showPreworkMentorEvaluate && (
								<Button
									type="primary"
									onClick={() => handleEvaluateClick(record)}
									className="w-fit"
								>
									{record.originalApproverRecommendationStatus ===
									PreworkReviewAction.PENDING
										? t('review')
										: t('view')}
								</Button>
							)}
						</div>
					) : (
						<div className="flex gap-2 justify-center">
							{showPreworkMentorEvaluate && (
								<>
									<Button
										type="primary"
										onClick={() => handleEvaluateClick(record)}
										className="w-fit"
										disabled={record.disableViewAction}
									>
										{record.originalMentorPreworkStatus ===
										PreworkCompletionStatus.COMPLETED
											? t('view')
											: t('review')}
									</Button>
									{showInfoIcon && (
										<Tooltip
											className="text-gray-400 text-base"
											title={t('resubmitAfter', { resubmitDate })}
										>
											<InfoCircleFilled className="text-xl" />
										</Tooltip>
									)}
								</>
							)}
						</div>
					)}
				</Space>
			);
		},
	});

	const columns = [...baseColumns];

	return (
		<>
			<Table
				columns={columns}
				dataSource={applicationTableData}
				pagination={tablePaginationConfig}
				scroll={{ x: 'max-content' }}
				rowClassName={() => 'border-b border-gray-300 bg-white'}
				className="w-full overflow-x-auto !border-separate !border-spacing-y-0"
			/>
		</>
	);
};
